(ns shared.core.components.google-maps.data
  (:require [shared.core.util :as util]
            [shared.core.config :as config]
            [shared.core.data :as d]
            [shared.core.reactive :as re]
            [clojure.string :as s]))

(defonce ^:private autocomplete-service-inst- (atom nil))
(defonce ^:private place-service-inst- (atom nil))
(defonce ^:private session-token- (atom nil))

;; Google models US territories as their own ISO 3166-1 countries (GU, PR, ...) rather than as
;; administrative_area_level_1 within the US, so those addresses have no state component at all.
;; USPS/AVS treat these same codes as state codes, so fall back to the country code.
(def us-territory-state-codes #{"AS" "GU" "MP" "PR" "UM" "VI"})

(when-not (exists? js/window.synqGMapsLoaded)
  (set! js/window.synqGMapsLoaded (fn [_] (js/console.log "Google Maps Loaded"))))

(defn place-with-address-parts [{:keys [address-components geometry street-address-2] :as place}]
  (if address-components
    (let [find-address-component-by-type (fn [component-type]
                                           (some->> address-components
                                                    (filter #(some (fn [t] (= t component-type)) (:types %)))
                                                    (first)))]
      (-> place
          (assoc :street-address-1 (some->> [(some-> (find-address-component-by-type "street_number") :long-name)
                                             (some-> (find-address-component-by-type "route") :short-name)]
                                            (filter #(not (s/blank? %)))
                                            (s/join " "))
                 :street-address-2 (or street-address-2
                                       (some-> (find-address-component-by-type "subpremise") :long-name))
                 :city (some-> (or (find-address-component-by-type "locality")
                                   (find-address-component-by-type "sublocality"))
                               :long-name)
                 :state (or (some-> (find-address-component-by-type "administrative_area_level_1") :short-name)
                            (some-> (find-address-component-by-type "country") :short-name (us-territory-state-codes)))
                 :postal-code (some-> (find-address-component-by-type "postal_code") :long-name)
                 :latitude (some-> geometry :location :lat)
                 :longitude (some-> geometry :location :lng))
          (dissoc :address-components :geometry :html-attributions)))
    place))

(re/reg-event-db
  :google-maps/add-maps-js-to-page re/middleware
  (fn [db]
    (let [script-tag (d/add-js-to-page (str "https://maps.googleapis.com/maps/api/js?key=" config/GOOGLE_MAPS_API_KEY "&callback=synqGMapsLoaded&libraries=places"))]
      (set! (.-async script-tag) true)
      db)))

(defn set-autocomplete-inst []
  (when (and (not @autocomplete-service-inst-) (exists? js/google.maps.places))
    (reset! autocomplete-service-inst- (js/google.maps.places.AutocompleteService.)))
  (when (and (not @session-token-) (exists? js/google.maps.places))
    (reset! session-token- (js/google.maps.places.AutocompleteSessionToken.))))

(defn set-place-inst []
  (when (and (not @place-service-inst-) (exists? js/google.maps.places))
    (reset! place-service-inst- (js/google.maps.places.PlacesService. (.createElement js/document "div")))))

(re/reg-event-db
  :google-maps/clear-session re/middleware
  (fn [db]
    (reset! session-token- nil)
    db))

(defn ->lat-lng [^js geo-loc]
  (let [lat (.-lat geo-loc)
        lng (.-lng geo-loc)]
    {:lat (when lat (lat))
     :lng (when lng (lng))}))

(re/reg-event-db
  :google-maps/load-place-result re/middleware
  (fn [db [{:keys [place-id fields success-event] :as args}]]
    (set-place-inst)
    (when (and @place-service-inst- success-event)
      (.getDetails ^js @place-service-inst-
                   (-> {:place-id place-id
                        :session-token @session-token-
                        :fields (or fields ["place_id" "address_component" "formatted_address" "geometry.location"])}
                       (util/->camelCaseKeywords)
                       (clj->js))
                   (fn [place status]
                     (let [ok? (= status (.-OK js/google.maps.places.PlacesServiceStatus))]
                       (when ok? (re/dispatch [:google-maps/clear-session]))
                       (re/dispatch (conj success-event
                                          {:ok? ok?
                                           :place (-> place
                                                      (js->clj)
                                                      (util/->kebab-case-keywords)
                                                      (update-in [:geometry :location] ->lat-lng))}
                                          args))))))
    db))

(re/reg-event-db
  :google-maps/load-autocomplete-results-resp re/middleware
  (fn [db [{:keys [ok? predictions]} {:keys [callback]}]]
    (if ok?
      (callback (some->> predictions
                         (map (fn [prediction]
                                (let [prediction (-> prediction (js->clj) (util/->kebab-case-keywords))
                                      {:keys [description place-id]} prediction]
                                  {:text description
                                   :value place-id
                                   :session-token @session-token-
                                   :prediction prediction})))))
      (callback (clj->js [])))
    db))

(re/reg-event-db
  :google-maps/load-autocomplete-results re/middleware
  (fn [db [{:keys [input location radius] :as args}]]
    (set-autocomplete-inst)
    (when (and @autocomplete-service-inst- input)
      (.then (.getPlacePredictions ^js @autocomplete-service-inst-
                                   (-> (cond-> {:input input
                                                :session-token @session-token-
                                                :options {:component-restrictions {:country "us"}}}
                                               (and (:lat location) (:lng location))
                                               (assoc :location (js/google.maps.LatLng. (:lat location)
                                                                                        (:lng location))
                                                      :radius (or radius 80467)) ;; Default is ~50 miles
                                               )
                                       (util/->camelCaseKeywords)
                                       (clj->js))
                                   (fn [predictions status]
                                     (re/dispatch [:google-maps/load-autocomplete-results-resp
                                                   {:ok? (= status (.-OK js/google.maps.places.PlacesServiceStatus))
                                                    :predictions (map util/->kebab-case-keywords predictions)}
                                                   args])))
             (fn [_])
             (fn [v] (js/console.error "Google Maps Autocomplete Failure:" v))))
    db))

(defn load-autocomplete-results [args]
  (re/dispatch [:google-maps/load-autocomplete-results args]))

(def load-autocomplete-results-debounced
  (util/debounce load-autocomplete-results 200))
